﻿import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import HeaderSection from '../components/Shared/HeaderSection'
import SegmentedControl from '../components/Shared/SegmentedControl'
import Kanban, { KANBAN_FILTER_FIELDS } from '../components/kanban/kanban'
import TableTickets from '../components/ticket/TableTickets'
import TicketSearchBar from '../components/ticket/TicketSearchBar'
import useMyTickets from '../hooks/useMyTickets'
import useTicketCatalogs from '../hooks/useTicketCatalogs'
import useTicketFilters from '../hooks/useTicketFilters'
import usePersistedState from '../hooks/usePersistedState'

/** El autor siempre es el usuario actual: ese filtro no aplica aquí. */
const KANBAN_FIELDS = KANBAN_FILTER_FIELDS.filter((f) => f !== 'authorId')
const TABLE_FIELDS = ['statusId', ...KANBAN_FIELDS]

const VIEW_OPTIONS = [
  { value: 'kanban', icon: 'fa-brands fa-trello', title: 'Vista Kanban' },
  { value: 'table', icon: 'fa-solid fa-table-list', title: 'Vista tabla' },
]

/**
 * Tickets que el usuario actual reportó (es su autor), sin importar a quién
 * estén asignados. Los asignados al usuario se gestionan en `/tickets`.
 * Misma estructura que Gestión de Tickets: cabecera compacta, una barra con
 * búsqueda y vista, y el contenido ocupando el alto disponible.
 */
const MyTickets = () => {
  const navigate = useNavigate()
  const { tickets, loading } = useMyTickets()
  const [view, setView] = usePersistedState('myTickets.view', 'table')
  const catalogs = useTicketCatalogs()
  const search = useTicketFilters(tickets, catalogs)
  const isTable = view === 'table'

  return (
    <div className="page-fill">
      <HeaderSection
        compact
        title="Mis Tickets"
        subtitle="Tickets que he reportado y su estado"
        icon="fa-solid fa-list-ul"
        buttonText="Nuevo ticket"
        buttonClass="btn btn-primary"
        onButtonClick={() => navigate('/nuevo-ticket')}
      />

      <div className="mt-toolbar mb-3">
        <TicketSearchBar
          filters={search.filters}
          onChange={search.setFilter}
          onClear={search.clearFilters}
          options={search.options}
          activeCount={search.activeCount}
          fields={isTable ? TABLE_FIELDS : KANBAN_FIELDS}
        >
          <span className="mt-count" aria-live="polite">
            {loading ? (
              <span className="spinner-border spinner-border-sm text-primary" />
            ) : (
              <>
                <strong>{search.filtered.length}</strong>
                {search.activeCount > 0 && ` de ${tickets.length}`}
              </>
            )}
          </span>
          <SegmentedControl
            ariaLabel="Vista"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
        </TicketSearchBar>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          className="mt-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {isTable ? (
            <TableTickets
              items={search.filtered}
              totalItems={tickets.length}
              storageKey="myTickets.table.columns"
            />
          ) : (
            <Kanban tickets={search.filtered} disableDrag showSearch={false} />
          )}
        </motion.div>
      </AnimatePresence>

      <style>{`
        .mt-content { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
        .mt-toolbar {
          flex-shrink: 0;
          padding: 8px;
          border: 1px solid var(--gm-border);
          border-radius: 12px;
          background: var(--gm-surface);
        }
        .mt-toolbar .tsb-search { background: var(--gm-surface-2); }
        .mt-count {
          min-width: 28px; font-size: 0.8rem; color: var(--gm-text-muted);
          font-variant-numeric: tabular-nums; text-align: right;
        }
        .mt-count strong { color: var(--gm-text); }
      `}</style>
    </div>
  )
}

export default MyTickets
